import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Col, Form, Row, Space, Spin, Typography } from 'antd'
import { groupBy, includes, isEmpty } from 'lodash'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormCheckbox,
  FormLazyListTreeSelect,
} from '../../../common/components/sections'
import { PERMISSIONS } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { idPropType, treeOptionPropType } from '../../../common/prop-types'
import { flattenTreeStructure } from '../../../common/utils'

import { useUpdateIngredientComponentDetailsMutation } from '../../hooks'
import { setIngredientPageIsDirty } from '../../redux/actions'
import { IngredientComponentIngredientFormListItem } from '../sections'

import { ingredientComponentDetailsFormInitialValues } from './initial-values'
import { validateComponentIngredient } from './validators'

const IngredientComponentDetailsForm = ({
  ingredientId,
  isWholeFood,
  isVague,
  componentIngredients,
  ingredientOptions,
  groupedFlatTreeDataByValue,
}) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.INGREDIENT.SAVE_COMPONENT_INGREDIENTS,
  ])

  const [
    updateIngredientComponentDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateIngredientComponentDetailsMutation()

  const initialValues = useMemo(
    () =>
      ingredientComponentDetailsFormInitialValues({
        isWholeFood,
        isVague,
        componentIngredients,
      }),
    [isWholeFood, isVague, componentIngredients],
  )

  const checkboxRulesBuilder = useCallback(
    isWholeFood => [
      ({ getFieldValue }) => ({
        validator(_, value) {
          if (
            value &&
            !isEmpty(getFieldValue('definiteIngredientIds')) &&
            !isEmpty(getFieldValue('probableIngredientIds'))
          ) {
            return Promise.reject(
              new Error(
                `Must not have component ingredients to be ${
                  isWholeFood ? 'whole food' : 'vague'
                }`,
              ),
            )
          }

          return Promise.resolve()
        },
      }),
    ],
    [],
  )

  const isWholeFoodRules = useMemo(
    () => checkboxRulesBuilder(true),
    [checkboxRulesBuilder],
  )

  const isVagueRules = useMemo(
    () => checkboxRulesBuilder(false),
    [checkboxRulesBuilder],
  )

  const componentIngredientRules = useMemo(
    () => [
      ({ getFieldValue }) => ({
        validator(_, value) {
          const isWholeFood = getFieldValue('isWholeFood')
          const isVague = getFieldValue('isVague')

          if ((isWholeFood || isVague) && !isEmpty(value)) {
            return Promise.reject(
              Error(
                `Cannot add component ingredients if ingredient is ${
                  isWholeFood ? 'whole food' : 'vague'
                }`,
              ),
            )
          }

          return Promise.resolve()
        },
      }),
    ],
    [],
  )

  const definiteIngredientIdsItemRules = useMemo(
    () => [
      ({ getFieldValue }) => ({
        validator(_, value) {
          try {
            if (includes(value, ingredientId)) {
              throw new Error(`Can't select owning ingredient`)
            }

            validateComponentIngredient(
              value,
              'probableIngredientIds',
              getFieldValue,
            )

            return Promise.resolve()
          } catch (error) {
            return Promise.reject(error)
          }
        },
      }),
    ],
    [ingredientId],
  )

  const probableIngredientIdsItemRules = useMemo(
    () => [
      ({ getFieldValue }) => ({
        validator(_, value) {
          try {
            if (includes(value, ingredientId)) {
              throw new Error(`Can't select owning ingredient`)
            }

            validateComponentIngredient(
              value,
              'definiteIngredientIds',
              getFieldValue,
            )

            return Promise.resolve()
          } catch (error) {
            return Promise.reject(error)
          }
        },
      }),
    ],
    [ingredientId],
  )

  const flattenedIngredientOptionsGroupedById = useMemo(
    () => groupBy(flattenTreeStructure(ingredientOptions), 'value'),
    [ingredientOptions],
  )

  const itemRender = useCallback(
    ({
      field: { key, name, fieldKey, ...restField },
      shouldModify,
      treeData,
      onClear,
      itemRules,
      ...rest
    }) => (
      <Form.Item
        key={key}
        name={name}
        fieldKey={fieldKey}
        rules={itemRules}
        {...restField}
      >
        <IngredientComponentIngredientFormListItem
          shouldModify={shouldModify}
          name={name}
          treeData={treeData}
          onClear={onClear}
          flattenedIngredientOptionsGroupedById={
            flattenedIngredientOptionsGroupedById
          }
          {...rest}
        />
      </Form.Item>
    ),
    [flattenedIngredientOptionsGroupedById],
  )

  const handleSaveData = useCallback(
    data => updateIngredientComponentDetails(ingredientId, data),
    [ingredientId, updateIngredientComponentDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Ingredient details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [componentIngredients, isWholeFood, isVague, resetForm])

  useEffect(() => {
    dispatch(setIngredientPageIsDirty({ isComponentIngredientsDirty: isDirty }))
  }, [dispatch, isDirty])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="componentForm"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <Typography.Title level={3}>Component Ingredients</Typography.Title>

      <Row gutter={[16]}>
        <Col span={24}>
          <Space>
            <FormCheckbox
              disabled={!isFormEnabled}
              label="Whole Food"
              name="isWholeFood"
              rules={isWholeFoodRules}
            />
            <FormCheckbox
              disabled={!isFormEnabled}
              label="Vague"
              name="isVague"
              rules={isVagueRules}
            />
          </Space>
        </Col>
        <Col span={12}>
          <FormLazyListTreeSelect
            disabled={!isFormEnabled}
            form={form}
            name="definiteIngredientIds"
            label="Definite Ingredients"
            showCountItems
            showSearch
            treeData={ingredientOptions}
            newItemTreeSelectPlaceholder="Select a new definite ingredient"
            itemRules={definiteIngredientIdsItemRules}
            rules={componentIngredientRules}
            isDirty={isDirty}
            itemRender={itemRender}
            groupedFlatTreeDataByValue={groupedFlatTreeDataByValue}
            initialValue={initialValues.definiteIngredientIds}
          />
        </Col>
        <Col span={12}>
          <FormLazyListTreeSelect
            disabled={!isFormEnabled}
            form={form}
            name="probableIngredientIds"
            label="Probable Ingredients"
            showCountItems
            showSearch
            treeData={ingredientOptions}
            newItemTreeSelectPlaceholder="Select a new probable ingredient"
            itemRules={probableIngredientIdsItemRules}
            rules={componentIngredientRules}
            isDirty={isDirty}
            itemRender={itemRender}
            groupedFlatTreeDataByValue={groupedFlatTreeDataByValue}
            initialValue={initialValues.probableIngredientIds}
          />
        </Col>
      </Row>
      <FormButtons
        permissions={[PERMISSIONS.INGREDIENT.SAVE_COMPONENT_INGREDIENTS]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

IngredientComponentDetailsForm.propTypes = {
  ingredientId: idPropType.isRequired,
  componentIngredients: PropTypes.arrayOf(PropTypes.object),
  isWholeFood: PropTypes.bool,
  isVague: PropTypes.bool,
  ingredientOptions: PropTypes.arrayOf(treeOptionPropType.isRequired)
    .isRequired,
  groupedFlatTreeDataByValue: PropTypes.object.isRequired,
}

IngredientComponentDetailsForm.defaultProps = {
  componentIngredients: [],
  isWholeFood: false,
  isVague: false,
}

export default IngredientComponentDetailsForm
